import React from "react";
import { Card, Row, Col, Tag, Divider, Progress } from "antd";
import { Flex, Button, Text, Avatar } from "@fluentui/react-northstar";
import {
  CheckmarkCircleIcon,
  EditIcon,
  RetryIcon,
  AcceptIcon,
  EyeIcon,
} from "@fluentui/react-northstar";
import { ClockCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { useIntelligentGrading } from "../../../../context/useIntelligentGrading";
import { useTranslation } from "react-i18next";

const brandColor = "#6264a7";

const formatTime = (timeTaken) => {
  if (typeof timeTaken !== "number" || isNaN(timeTaken)) return "-";
  const minutes = Math.floor(timeTaken / 60);
  const seconds = Math.floor(timeTaken % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes} : ${seconds}`;
};

const StudentGradingCard = ({
  student,
  isSelected,
  onClick,
  onApprove,
  onRegenerate,
  onEdit,
  onQuickView,
}) => {
  const { t } = useTranslation();
  const { isLoading, allAssignmentSubmissions, setAnnotations } =
    useIntelligentGrading();
  const translation = t("assignment").summary.batch;
  // Defensive checks for new format
  const {
    image,
    name,
    submissionDate,
    grade,
    confidence,
    timeTaken,
    rubrics = [],
    status,
    statusColor = [],
    submissionStatus,
    overWrite,
  } = student || {};

  const isGradable = submissionStatus === "Grades Pending";

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "#52c41a";
      case "Grades Pending":
        return "#1890ff";
      case "Resubmit":
        return "#f5222d";
      default:
        return "#faad14";
    }
  };

  const handleAccept = (e) => {
    e.stopPropagation();
    onApprove();
  };

  const handleRegenerate = (e) => {
    e.stopPropagation();
    onRegenerate();
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit();
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    const submission = allAssignmentSubmissions.find(
      (submission) => submission.id === student.id
    );
    setAnnotations(
      submission.submissionStatus === "Completed"
        ? submission.teacherGradingResult.annotations
        : submission.aiGradingResult.annotations
    );
    onQuickView();
  };

  return (
    <Card
      bordered
      onClick={onClick}
      style={{
        cursor: "pointer",
        borderColor: isSelected ? brandColor : undefined,
        // boxShadow: isSelected
        //   ? "0 0 0 2px #6264a7, 0 2px 8px rgba(0,0,0,0.08)"
        //   : "",
        transition: "box-shadow 0.2s, border-color 0.2s",
        background: isSelected ? "#f4f3ff" : "#fff",
        minHeight: "323px",
        height: "100%",
      }}
      bodyStyle={{
        padding: 16,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      hoverable
    >
      <Flex column fill>
        <Row align="middle" gutter={8}>
          <Col>
            <Avatar image={image} size="medium" />
          </Col>
          <Col flex="auto">
            <Text strong>{name}</Text>
            <br />
            {submissionDate && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {translation.studentCard.submittedOn}:{" "}
                {new Date(submissionDate).toLocaleString()}
              </Text>
            )}
          </Col>
          {grade && (
            <Col>
              <Tag
                color={
                  grade?.startsWith("A")
                    ? "green"
                    : grade?.startsWith("B")
                      ? "blue"
                      : grade?.startsWith("D")
                        ? "red"
                        : "gold"
                }
              >
                {grade}
              </Tag>
            </Col>
          )}
        </Row>
        <Flex.Item grow>
          <div>
            {(isGradable || submissionStatus === "Completed") &&
              student.aiGradingStatus === "Completed" ? (
              <>
                <Row
                  align="middle"
                  justify="space-between"
                  style={{ margin: "8px 0" }}
                  gutter={8}
                >
                  <Col>
                    <Flex vAlign="center">
                      <CheckmarkCircleIcon
                        style={{
                          color:
                            confidence > 80
                              ? "#52c41a"
                              : confidence > 60
                                ? "#faad14"
                                : "#ff4d4f",
                          marginRight: 4,
                        }}
                      />
                      <Text style={{ fontSize: 13 }}>
                        {translation.studentCard.aiConfidence}: {confidence}%
                      </Text>
                    </Flex>
                  </Col>
                  {!overWrite && (
                    <Col>
                      <span style={{ marginLeft: 12, marginRight: 4 }}>
                        <ClockCircleOutlined />
                      </span>
                      <Text style={{ fontSize: 13 }}>
                        {" "}
                        {formatTime(timeTaken)}
                      </Text>
                    </Col>
                  )}
                </Row>
                <div style={{ margin: "8px 0" }}>
                  {rubrics.map((rubric, i) => (
                    <div key={i} style={{ marginBottom: 5 }}>
                      {/* Rubric Title */}
                      <Text style={{ fontSize: 13, fontWeight: 500 }}>
                        {rubric.label}
                      </Text>
                      {/* Progress Bar */}
                      <Flex gap="gap.smaller" vAlign="center" fill>
                        <Progress
                          percent={Math.round(
                            (rubric.score / rubric.max) * 100
                          )}
                          showInfo={false}
                          strokeColor={rubric.color || brandColor}
                          style={{ width: "100%" }}
                          size="small"
                        />
                        {/* Points below */}
                        <Text style={{ fontSize: 13, color: "#888" }}>
                          {rubric.score}/{rubric.max}
                        </Text>
                      </Flex>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Flex column vAlign="center" hAlign="center" fill>
                <Text type="secondary" style={{ fontSize: 15 }}>
                  {submissionStatus === "Pending"
                    ? translation.studentCard.noGradingData
                    : submissionStatus === "Resubmit" ? "Resubmission is Pending" : student.aiGradingStatus === "Pending"
                      ? "AI Grading is currently Pending"
                      : student.aiGradingStatus === "Failed"
                        ? "Ai grading generation is Failed"
                        : student.aiGradingStatus === "Processing" &&
                        "AI Grading is currently in Progress"}
                </Text>
              </Flex>
            )}
            {/* Status tags if present and is array */}
            {Array.isArray(status) && status.length > 0 && (
              <div style={{ margin: "8px 0" }}>
                {status.map((stat, i) => {
                  let icon = null;
                  switch (stat) {
                    case "Ready to Approve":
                      icon = (
                        <CheckmarkCircleIcon
                          style={{ marginRight: 4, fontSize: 12 }}
                        />
                      );
                      break;
                    case "Needs Review":
                      icon = (
                        <EditIcon style={{ marginRight: 4, fontSize: 12 }} />
                      );
                      break;
                    case "Unclear Thesis":
                      icon = (
                        <EditIcon style={{ marginRight: 4, fontSize: 12 }} />
                      );
                      break;
                    case "Off Topic":
                      icon = (
                        <EditIcon style={{ marginRight: 4, fontSize: 12 }} />
                      );
                      break;
                    case "Late":
                      icon = (
                        <ClockCircleOutlined
                          style={{ marginRight: 4, fontSize: 12 }}
                        />
                      );
                      break;
                    case "High Confidence":
                      icon = (
                        <AcceptIcon style={{ marginRight: 4, fontSize: 12 }} />
                      );
                      break;
                    case "Exemplary":
                      icon = (
                        <span style={{ marginRight: 4, fontSize: 12 }}>🌟</span>
                      );
                      break;
                    default:
                      icon = null;
                  }
                  return (
                    <Tag
                      key={stat}
                      color={statusColor[i]}
                      style={{
                        marginBottom: 4,
                        display: "inline-flex",
                        alignItems: "center",
                        height: "22px",
                        padding: "0 8px",
                        fontSize: "12px",
                        lineHeight: "20px",
                      }}
                    >
                      {icon}
                      {stat}
                    </Tag>
                  );
                })}
              </div>
            )}
          </div>
        </Flex.Item>
        <div>
          <Divider style={{ margin: "8px 0" }} />
          <Flex space="between" vAlign="center" style={{ marginTop: 4 }}>
            <Button
              text
              icon={<EyeIcon />}
              iconOnly
              title={translation.studentCard.viewSubmissionDetails}
              onClick={handleQuickView}
              content={translation.studentCard.viewDetails}
              disabled={
                submissionStatus === "Pending" ||
                submissionStatus === "Resubmit" ||
                ["Failed", "Pending", "Processing"].includes(
                  student.aiGradingStatus
                )
              }
              primary
            />
            {submissionStatus === "Completed" ? (
              <Tag color={"green"}>{translation.status.completed}</Tag>
            ) : submissionStatus === "Pending" ||
              student.aiGradingStatus === "Pending" ? (
              <Tag color={"gold"}>{translation.status.pending}</Tag>
            ) : submissionStatus === "Resubmit" ||
              student.aiGradingStatus === "Failed" ? (
              <Tag color={"red"}>
                {student.aiGradingStatus === "Failed"
                  ? "Failed"
                  : translation.status.resubmit}
              </Tag>
            ) : student.aiGradingStatus === "Processing" ? (
              <Tag
                icon={<SyncOutlined spin twoToneColor="#faad14" />}
                color={"blue"}
                style={{ display: "flex", alignItems: "center" }}
              >
                In Progress
              </Tag>
            ) : (
              <Flex gap="gap.smaller">
                <Button
                  data-cy="approve-submission"
                  text
                  icon={<AcceptIcon />}
                  style={{ color: "#52c41a" }}
                  iconOnly
                  title={translation.studentCard.approveThisSubmission}
                  onClick={handleAccept}
                  disabled={isLoading || submissionStatus === "Completed"}
                />
                <Button
                  data-cy="edit-grading-results"
                  text
                  icon={<EditIcon />}
                  iconOnly
                  title={translation.studentCard.editGradingResults}
                  onClick={handleEdit}
                  disabled={!isGradable}
                  primary
                />
                <Button
                  data-cy="regenerate-ai-feedback"
                  text
                  icon={<RetryIcon />}
                  primary
                  iconOnly
                  title={translation.studentCard.regenerateAiFeedback}
                  onClick={handleRegenerate}
                  disabled={isLoading || submissionStatus === "Completed"}
                />
              </Flex>
            )}
          </Flex>
        </div>
      </Flex>
    </Card>
  );
};

export default StudentGradingCard;
